import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField } from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useForm } from "react-hook-form";
import { useCreateTaskMutation } from "../../../store";
import type { CreateTaskData } from "../../../domain";
import type { CreateTaskDialogProps } from "../types/CreateTaskDialogProps";

/**
 * Collects a new task for submission to the demo API.
 *
 * @param props - Dialog close callback
 * @returns Validated task form with mutation feedback
 */
export const CreateTaskDialog = ({ onClose }: CreateTaskDialogProps) => {
  const [createTask, { isLoading, isError }] = useCreateTaskMutation();
  const { register, handleSubmit, formState: { errors } } = useForm<CreateTaskData>({
    defaultValues: {
      title: "",
      description: "",
      priority: "medium",
      status: "pending",
      dueDate: null,
      tags: [],
    },
  });

  /** Submits validated task data to the demo API. */
  const handleCreateTask = async (data: CreateTaskData): Promise<void> => {
    if (isLoading) return;
    const result = await createTask({ ...data, title: data.title.trim() });
    if ("data" in result) onClose();
  };

  return (
    <Dialog open onClose={isLoading ? undefined : onClose} aria-labelledby="create-task-title" fullWidth maxWidth="sm">
      <form onSubmit={handleSubmit(handleCreateTask)} noValidate>
        <DialogTitle id="create-task-title">New Task</DialogTitle>
        <DialogContent>
          {isError && <Alert severity="error" sx={{ mb: 2 }}>Task could not be created. Please try again.</Alert>}
          <TextField
            autoFocus fullWidth margin="normal" label="Title" disabled={isLoading}
            error={Boolean(errors.title)} helperText={errors.title?.message}
            {...register("title", {
              required: "Enter a task title",
              pattern: { value: /\S/, message: "Enter a task title" },
            })}
          />
          <TextField fullWidth margin="normal" label="Description" multiline minRows={3} disabled={isLoading} {...register("description")} />
          <TextField fullWidth margin="normal" label="Priority" select defaultValue="medium" disabled={isLoading} {...register("priority")}>
            <MenuItem value="low">Low</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="high">High</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isLoading}>Cancel</Button>
          <Button type="submit" variant="contained" startIcon={<AddIcon />} disabled={isLoading}>
            {isLoading ? "Creating task" : "Create task"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
